import { SquareMinus } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { CreateGroup } from '@/features/record-groups/components/createGroup'
import { useAllowedActions, usePermission } from '@/hooks/usePermission'
import { useRecordGroupSearch } from '@/hooks/useRecordGroups'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { RecordGroupDetail, RecordGroupListItem } from '@/types/api/record-group'

import { LoadingRow } from './LoadingRow'
import { RecordTreeRow } from './RecordTreeRow'
import { GROUP_PAGE_SIZE } from './constants'

type ExplorerGroup = RecordGroupDetail | RecordGroupListItem

interface TreeState {
  currentGroupId: string
  lockedResourceIds: Set<string>
  expandedGroupIds: string[]
  onToggleExpanded: (groupId: string) => void
  onCollapseFolder: (groupId: string) => void
  onCollapseChildGroups: () => void
}

export function RecordTreeNode({
  group,
  depth,
  treeState,
  isMainParent = false,
}: {
  group: ExplorerGroup
  depth: number
  treeState: TreeState
  isMainParent?: boolean
}) {
  const expanded = treeState.expandedGroupIds.includes(group.id)
  const ancestors = 'path' in group ? group.path : group.ancestors
  const breadcrumbPath = [...ancestors, { id: group.id, name: group.name, traceOnly: group.traceOnly }]
  const canCreateGroups = usePermission().can(PERMISSION_KEYS.RECORD_GROUP_CREATE)
  const groupActions = useAllowedActions('RECORD_GROUP', group.id)
  const canCreateChild = canCreateGroups && groupActions.can(PERMISSION_KEYS.RECORD_GROUP_UPDATE) && !group.traceOnly

  return (
    <>
      <RecordTreeRow
        id={group.id}
        label={group.name}
        depth={depth}
        active={treeState.currentGroupId === group.id}
        breadcrumbPath={breadcrumbPath}
        locked={treeState.lockedResourceIds.has(group.id)}
        traceOnly={group.traceOnly}
        isMainParent={isMainParent}
        expanded={expanded}
        onToggle={() => treeState.onToggleExpanded(group.id)}
        actions={(
          <div className="flex items-center gap-1">
            {canCreateChild && (
              <CreateGroup
                parentId={group.id}
                buttonLabel={`Add child group under ${group.name}`}
                buttonVariant="ghost"
                buttonClassName="h-7 w-7"
                iconOnly
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label={isMainParent ? 'Collapse child groups' : `Collapse folder ${group.name}`}
              title={isMainParent ? 'Collapse child groups' : `Collapse folder ${group.name}`}
              onClick={() => isMainParent ? treeState.onCollapseChildGroups() : treeState.onCollapseFolder(group.id)}
            >
              <SquareMinus className="size-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      />
      {expanded && (
        <RecordTreeContents
          parentId={group.id}
          depth={depth + 1}
          treeState={treeState}
        />
      )}
    </>
  )
}

function RecordTreeContents({ parentId, depth, treeState }: { parentId: string; depth: number; treeState: TreeState }) {
  const [page, setPage] = useState(0)
  const { data, isLoading, isError } = useRecordGroupSearch({
    parentId,
    scope: 'CHILDREN',
    sort: 'NAME',
    dir: 'ASC',
    page,
    size: GROUP_PAGE_SIZE,
  })

  if (isLoading && !data) return <LoadingRow level={depth} />
  if (isError) {
    return (
      <p className="py-2 text-sm text-destructive" style={{ paddingLeft: 42 + depth * 14 }} role="alert">
        Could not load child groups.
      </p>
    )
  }

  const children = data?.content ?? []

  return (
    <div role="group">
      {children.map((child) => (
        <RecordTreeNode key={child.id} group={child} depth={depth} treeState={treeState} />
      ))}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 py-2 pr-2 text-xs text-muted-foreground">
          <span>Page {data.page + 1} of {data.totalPages}</span>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
            Previous
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2" disabled={page + 1 >= data.totalPages} onClick={() => setPage((value) => value + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
